<template lang="pug">
Card.select-mark(
  class="bg-white/70 dark:bg-gray-900/70 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-3xl hover:shadow-lg relative cursor-pointer transition-all" 
  @click="$emit('select-plan', planId)" 
  :class="{selected: selectedPlan === planId}"
)
  PTag(
    class="absolute right-4 top-4" 
    type="success" 
    size="small" 
    v-if="isCurrentPlan"
  ) {{ $t('current_plan') }}
  
  CardContent(class="px-4 pt-6 pb-4")
    h2(class="text-base font-semibold") {{ planName }}
    
    .pricetag(class="mb-2")
      h1
        small {{ feeList.currency }}
        span.original-price(v-if="selectedTime >= 6") {{ feeList[planId] }}
        span(:class="{'discounted-price': selectedTime >= 6}") {{ calcPrice(feeList[planId]) }}
        small /{{ $t('month') }}
    
    .content(class="text-xs")
      component(
        v-for="(feature, index) in features" 
        :key="index"
        :is="renderFeature(feature, index)"
      )
        
    Icon(name="material-symbols:check-small-rounded" class="checkmark")
</template>

<script lang="ts" setup>
import { h } from 'vue'
import { Card, CardContent } from '@/components/ui/card'

const props = defineProps<{
  planId: number | string
  selectedPlan: number | string
  currentPlanId?: number
  currentService?: any
  selectedTime: number
  feeList: any
  calcPrice: (price: number) => number
  features: Array<any>
}>()

defineEmits(['select-plan'])

const $t = useI18n().t

// Compute plan name from translation
const planName = computed(() => $t(`plan_names.${props.planId}`))

// Check if this is the current plan
const isCurrentPlan = computed(() => {
  if (props.currentService?.extra && 
      (props.currentService?.status === 'on' || props.currentService?.extra?.buy_length > 1) && 
      props.currentPlanId === props.planId) {
    return true
  }
  return props.currentPlanId === props.planId
})

// Render feature based on its structure
function renderFeature(feature: any, index: number) {
  const isLastItem = index === props.features.length - 1
  const isFirstItem = index === 0
  const className = [
    isLastItem ? '' : 'mb-0.5',
    isFirstItem ? 'first-feature' : ''
  ].filter(Boolean).join(' ')
  const marker = index === 0
    ? []
    : [h('span', { class: 'feature-marker', 'aria-hidden': 'true' }, '•')]
  
  // Simple string feature
  if (typeof feature === 'string') {
    return h('h5', { class: className }, [...marker, feature])
  }
  
  // Complex feature with type
  if (feature.type === 'thin') {
    return h('h5', { class: className }, [
      ...marker,
      h('span', { class: 'subheader thin' }, feature.text)
    ])
  }
  
  // Feature with highlights
  if (feature.highlight) {
    const elements: any[] = []
    
    if (feature.beforeHighlight) {
      elements.push(feature.beforeHighlight + ' ')
    }
    
    elements.push(h('b', { class: 'highlight-underline' }, feature.highlight))
    
    if (feature.afterHighlight) {
      elements.push(' ' + feature.afterHighlight)
    }
    
    return h('h5', { class: className }, [...marker, ...elements])
  }
  
  // Default case - just text
  return h('h5', { class: className }, [...marker, feature.text || feature])
}
</script>

<style lang="postcss" scoped>
.select-mark {
  position: relative;
  overflow: visible;
  
  &.selected {
    transform: translateY(-2px);
    box-shadow: 
      0 0 0 3px rgb(126, 211, 33),
      0 20px 40px rgba(126, 211, 33, 0.2);
      
    .checkmark {
      display: block !important;
    }
  }
  
  &:hover {
    transform: translateY(-4px);
  }
}

.checkmark {
  position: absolute;
  bottom: 16px;
  right: 16px;
  color: rgb(126, 211, 33);
  font-size: 24px;
  display: none;
}

.pricetag {
  h1 {
    font-size: 2rem;
    line-height: 1.2;
    margin: 0.5rem 0;
    
    small {
      font-size: 1rem;
      font-weight: normal;
      opacity: 0.8;
    }
    
    .original-price {
      text-decoration: line-through;
      opacity: 0.5;
      margin-right: 0.5rem;
    }
    
    .discounted-price {
      color: rgb(126, 211, 33);
    }
  }
}

.content {
  :deep(h5) {
    margin: 0;
    font-weight: normal;
    line-height: 1.5;
    
    &.mb-0-5 {
      margin-bottom: 0.125rem;
    }

    &.first-feature {
      font-weight: 600;
    }
    
    .subheader {
      &.thin {
        font-weight: 300;
        opacity: 0.9;
      }
    }
    
    .highlight-underline {
      font-weight: 600;
      text-decoration: underline;
      color: rgb(126, 211, 33);
    }

    .feature-marker {
      display: inline-block;
      margin-right: 0.5rem;
      color: rgb(126, 211, 33);
      font-weight: 600;
    }
  }
}
</style>
